import { useRef, useState, type FormEvent } from 'react';

import { matchSuggestions } from './matchSuggestions.ts';
import type { Suggestion } from './itemsApi.ts';

interface AddItemBarProps {
  /**
   * The household's remembered names, ranked — a pool to search, not a row to draw. The
   * endpoint sends far more than fits on screen precisely so that typing can narrow it
   * without a request; see `matchSuggestions` and `SuggestionPoolLimit`.
   */
  suggestions: Suggestion[];
  onAdd: (name: string) => void;
  /** Erase this name from the group's history so it is never suggested again. */
  onForgetSuggestion?: (name: string) => void;
  /**
   * Somebody has reached for the add box, so the keyboard is on its way up and the list
   * above needs to move with it — see revealItem.
   *
   * Deliberately not named for focus, because focus is not what this is about. Dismissing
   * the keyboard does not always blur the field, so the second tap of the add box very
   * often lands on an input that is *already* focused: the keyboard comes back up, no
   * focus event is fired, and a handler listening for one never runs again for the rest
   * of the visit. Wired to the tap as well, which happens either way.
   */
  onReachForInput?: () => void;
}

/** How long a press has to be held before it means "options" rather than "add". */
const LongPressMs = 500;

/**
 * How many chips the row draws. The pool behind it is far larger — this is what fits
 * above the add box on a phone, and it is the same number in both states.
 */
const ChipLimit = 8;

/**
 * The add box and its row of one-tap chips.
 *
 * Tapping a chip adds immediately — no modal, no confirm, and no keyboard. Typing and
 * pressing enter also adds immediately, but leaves the field focused and empty, because
 * someone who has just typed is very likely to type again.
 *
 * **Typing narrows the row to what matches it.** The chips at rest are the things bought
 * most often, which is exactly the wrong answer for anything else the household buys —
 * the ninth most popular thing, or the one bought twice in March, had to be typed out in
 * full. The names to search were already being ranked and cached on the server, so this
 * costs one larger response per list rather than a request per keystroke; see
 * `matchSuggestions`, which is where the matching lives and why it lives on the phone.
 *
 * Enter and the + button always add **what was typed**, never the top match. A key that
 * quietly adds a different word from the one on the screen is the single thing that would
 * make this row untrustworthy.
 *
 * Getting rid of a bad chip (a typo like "Miljk") is a long press, or a right-click on
 * a desktop. Deliberately not an × on every chip: the entire value of this row is that a
 * chip is one tap, and putting a second target inside each one invites the mis-tap that
 * adds nothing and deletes something. A long press cannot be hit by accident.
 *
 * It erases the name outright — there is no hidden list to curate afterwards, because a
 * typo is not worth keeping a record of and retyping it is quicker than finding it.
 */
export function AddItemBar({
  suggestions,
  onAdd,
  onForgetSuggestion,
  onReachForInput,
}: AddItemBarProps) {
  const [name, setName] = useState('');
  const [pendingForget, setPendingForget] = useState<string | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const pressTimer = useRef<number | null>(null);
  // Set when a long press fires, so the click that follows does not also add the item.
  const suppressClick = useRef(false);

  // Derived during render, deliberately: filtering a few dozen strings costs nothing, and
  // mirroring a derived list into state through an effect is what the React Compiler rules
  // in this repo reject — as well as being a second copy that can disagree with the box.
  const isSearching = name.trim().length > 0;
  const shown = matchSuggestions(suggestions, name, ChipLimit);

  /**
   * Adds the item, and decides whether the person is still typing.
   *
   * `keepTyping` is what puts the cursor back in the box: tapping the + button blurs it,
   * which on a phone takes the keyboard down, and people add three things in a row far
   * more often than one. A chip is the opposite — it is a whole add in one tap, by
   * someone who has chosen not to type — so raising the keyboard over the list there is
   * furniture appearing in answer to a tap that asked for none.
   */
  function submit(value: string, { keepTyping = true }: { keepTyping?: boolean } = {}) {
    const trimmed = value.trim();
    if (trimmed.length === 0) {
      return;
    }

    onAdd(trimmed);
    setName('');

    if (keepTyping) {
      inputRef.current?.focus();
    }
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    submit(name);
  }

  function beginPress(suggestionName: string) {
    if (!onForgetSuggestion) {
      return;
    }

    cancelPress();

    pressTimer.current = window.setTimeout(() => {
      suppressClick.current = true;
      setPendingForget(suggestionName);
      // Confirms the press registered without needing to look at the screen.
      navigator.vibrate?.(30);
    }, LongPressMs);
  }

  function cancelPress() {
    if (pressTimer.current !== null) {
      window.clearTimeout(pressTimer.current);
      pressTimer.current = null;
    }
  }

  return (
    <div className="add-bar">
      {pendingForget !== null ? (
        <div className="chip-confirm" role="alertdialog" aria-label="Forget this suggestion?">
          <span className="chip-confirm__text">
            Forget &ldquo;{pendingForget}&rdquo;?
          </span>
          <button
            type="button"
            className="button button--danger button--small"
            onClick={() => {
              onForgetSuggestion?.(pendingForget);
              setPendingForget(null);
            }}
          >
            Forget
          </button>
          <button
            type="button"
            className="button button--quiet button--small"
            onClick={() => setPendingForget(null)}
          >
            Keep
          </button>
        </div>
      ) : (
        // Rendered whenever there is a history to search, not whenever there is something
        // to show. A query that matches nothing leaves the row empty and its height
        // reserved: the add bar is sticky at the bottom of the screen, so a row that
        // collapsed would drag the input down *under the thumb typing into it* — and it
        // would move the floor `parkAboveAddBar` measures the list against, mid-keystroke.
        // A household with no history at all still gets no row, as before: reserving space
        // that can never fill is furniture.
        suggestions.length > 0 && (
          <div
            className="chips"
            role="group"
            aria-label={
              isSearching ? 'Matching things you often buy' : 'Add something you often buy'
            }
          >
            {shown.map((suggestion) => (
              <button
                key={suggestion.name}
                type="button"
                className="chip"
                onClick={() => {
                  if (suppressClick.current) {
                    suppressClick.current = false;
                    return;
                  }
                  // The chip rule, inverted for a searched chip — and for the same
                  // reason. At rest a chip is a whole add by someone who chose not to
                  // type, so raising the keyboard answers a tap that asked for none.
                  // Someone who tapped a *match* is mid-word with the keyboard already
                  // up, and is as likely to type the next thing as anyone who pressed
                  // enter; tapping a button moves focus off the input, so without this
                  // the keyboard drops on every match taken.
                  submit(suggestion.name, { keepTyping: isSearching });
                }}
                onPointerDown={() => beginPress(suggestion.name)}
                onPointerUp={cancelPress}
                onPointerLeave={cancelPress}
                onPointerCancel={cancelPress}
                // The desktop equivalent of a long press. Without it, someone with a
                // mouse has to hold the button down and wait, which nobody guesses.
                onContextMenu={(event) => {
                  if (onForgetSuggestion) {
                    event.preventDefault();
                    setPendingForget(suggestion.name);
                  }
                }}
              >
                {suggestion.name}
              </button>
            ))}
          </div>
        )
      )}

      <form className="add-bar__form" onSubmit={handleSubmit}>
        <input
          ref={inputRef}
          className="add-bar__input"
          value={name}
          onChange={(event) => setName(event.target.value)}
          onFocus={onReachForInput}
          // Both, not either: focus alone misses a tap on an already-focused box, and a
          // click alone misses the keyboard arriving by Tab or by autofocus.
          onClick={onReachForInput}
          placeholder="Add something"
          aria-label="Add an item"
          // A shopping list is full of proper nouns and brand names; the browser
          // correcting "Aldi" to "Audi" helps nobody.
          autoCorrect="off"
          autoCapitalize="sentences"
          spellCheck={false}
          enterKeyHint="done"
          maxLength={120}
        />

        <button
          className="add-bar__submit"
          type="submit"
          disabled={name.trim().length === 0}
          aria-label="Add"
        >
          <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true" focusable="false">
            <path
              d="M12 5v14M5 12h14"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </form>
    </div>
  );
}
